import { Flex, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, FormProvider, Resolver, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '../../../components/layout/page';
import { PhysicianDto, UpdatePhysicianDto, api } from '../../../lib/api';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { Tag } from '@/components/ui/tag';
import { Button } from '@/components/ui/button';
import { LinkButton } from '@/components/ui/link-button';
import { Divider } from '@/components/ui/Divider';
import { Card } from '@/components/ui/card';
import { PhoneInput } from '@/components/form/phone-input';
import { FaxInput } from '@/components/form/fax-input';
import Address from '@/components/address';
import { z } from 'zod';
import { phoneSchema, faxOptionalSchema, addressValidationSchema } from '@musa/lib';
import { zodResolver } from '@hookform/resolvers/zod';

const validationSchema: Resolver<UpdatePhysicianDto> = zodResolver(
    z.object({
        addresses: z.array(
            z.object({
                id: z.string().optional(),
                physicianId: z.string(),
                address: addressValidationSchema,
                phone: phoneSchema,
                fax: faxOptionalSchema,
                type: z.string(),
            }),
        ),
    }),
);

export function PhysicianForm() {
    const navigate = useNavigate();
    const params = useParams<'physicianId'>();

    const rhf = useForm<UpdatePhysicianDto>({
        defaultValues: {
            addresses: [
                {
                    address: {
                        address: '',
                        city: '',
                        state: '',
                        zipcode: '',
                    },
                    phone: '',
                    fax: '',
                    type: '',
                },
            ],
        },
        resolver: validationSchema,
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
    });

    const { fields } = useFieldArray({
        control: rhf.control,
        name: 'addresses',
    });

    // fetch data
    const { isFetched, data } = useQuery<PhysicianDto | void>({
        queryKey: ['physician', params.physicianId],
        queryFn: async () => {
            try {
                const { data, status } = await api.physician.get(params.physicianId as string);

                if (status !== 200 || !data) {
                    navigate('/settings/physicians');
                }

                return data;
            } catch {
                navigate('/settings/physicians');
            }
        },
        enabled: Boolean(params.physicianId),
    });

    useEffect(() => {
        if (data) {
            rhf.reset(data);
        }
    }, [data, rhf]);

    // update data
    const { mutate, isPending } = useMutation<unknown, unknown, UpdatePhysicianDto>({
        mutationFn: async (data) => {
            if (!params.physicianId) {
                return;
            }

            const response = await api.physician.edit(params?.physicianId, data);

            return response;
        },
        onSuccess: () => {
            navigate('/settings/physicians');
        },
    });

    if (!isFetched) {
        return (
            <Spinner
                borderWidth="4px"
                animationDuration="0.65s"
                css={{ '--spinner-track-color': 'purple.100' }}
                color="purple.500"
                size="lg"
            />
        );
    }

    return (
        <Page>
            <FormProvider {...rhf}>
                <form noValidate onSubmit={rhf.handleSubmit((values) => mutate(values))}>
                    <Flex gap="8" flexDirection="column" width="100%" css={{ '--field-label-width': 0 }}>
                        <Card>
                            <FieldGroup>
                                <Field orientation="horizontal" label="NPI:" justifyContent="start">
                                    <Text>{rhf.getValues('npi')}</Text>
                                </Field>

                                <Field orientation="horizontal" label="Credentials:" justifyContent="start">
                                    <Text>{rhf.getValues('credential')}</Text>
                                </Field>

                                <Field orientation="horizontal" label="PECOS:" justifyContent="start">
                                    <Tag
                                        colorPalette={rhf.getValues('pecos') ? 'green' : 'red'}
                                        size="sm"
                                        textTransform="uppercase"
                                        fontWeight="700">
                                        {rhf.getValues('pecos') ? 'enrolled' : 'not enrolled'}
                                    </Tag>
                                </Field>
                            </FieldGroup>
                        </Card>
                        {fields?.map(({ id }, index) => {
                            return (
                                <Card key={id}>
                                    <FieldGroup>
                                        <Address
                                            isRequired
                                            nameAddress={`addresses.${index}.address.address`}
                                            nameCity={`addresses.${index}.address.city`}
                                            nameState={`addresses.${index}.address.state`}
                                            nameZipCode={`addresses.${index}.address.zipcode`}
                                            validAddress={!!rhf.formState.errors.addresses?.[index]?.address?.address}
                                            validCity={!!rhf.formState.errors.addresses?.[index]?.address?.city}
                                            validState={!!rhf.formState.errors.addresses?.[index]?.address?.state}
                                            validZipCode={!!rhf.formState.errors.addresses?.[index]?.address?.zipcode}
                                            errorAddress={rhf.formState.errors.addresses?.[index]?.address?.address?.message}
                                            errorCity={rhf.formState.errors.addresses?.[index]?.address?.city?.message}
                                            errorState={rhf.formState.errors.addresses?.[index]?.address?.state?.message}
                                            errorZipCode={rhf.formState.errors.addresses?.[index]?.address?.zipcode?.message}
                                        />
                                        <Divider colorPalette="gray" />

                                        <FieldRow>
                                            <Field label="Phone">
                                                <Controller
                                                    control={rhf.control}
                                                    name={`addresses.${index}.phone`}
                                                    render={({ field: { value, onChange, ...props } }) => (
                                                        <PhoneInput
                                                            iconSize={16}
                                                            value={value || ''}
                                                            {...props}
                                                            onChange={(e) => onChange(e.target.value)}
                                                        />
                                                    )}
                                                />
                                            </Field>

                                            <Field label="Fax">
                                                <Controller
                                                    control={rhf.control}
                                                    name={`addresses.${index}.fax`}
                                                    render={({ field: { value, onChange, ...props } }) => (
                                                        <FaxInput
                                                            {...props}
                                                            value={value ?? ''}
                                                            onChange={(e) => onChange(e.target.value)}
                                                        />
                                                    )}
                                                />
                                            </Field>
                                        </FieldRow>
                                    </FieldGroup>
                                </Card>
                            );
                        })}

                        <Flex alignItems="flex-start" gap="4">
                            <Button type="submit" loading={isPending} loadingText="Saving">
                                Save
                            </Button>
                            <LinkButton to="/settings/physicians">Cancel</LinkButton>
                        </Flex>
                    </Flex>
                </form>
            </FormProvider>
        </Page>
    );
}
